import { useState, useEffect, useRef } from 'react';
import { X, Package, User, Check, Loader2 } from 'lucide-react';
import { api } from '../../../api/client';
import { hiRes } from '../shared';
import { Can } from '../../../lib/rbac';

interface LinkTrackingModalProps {
  tracking: string;
  sellerId: number;
  onClose: () => void;
  onLinked: () => void;
}

export function LinkTrackingModal({ tracking, sellerId, onClose, onLinked }: LinkTrackingModalProps) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRow, setConfirmRow] = useState<any | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 3) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res: any = await api.get(`/warehouse/search-orders?q=${encodeURIComponent(q.trim())}&seller_id=${sellerId}`);
        setResults(res.results || []);
      } catch (e: any) {
        setError(e.message || 'Error en búsqueda');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, sellerId]);

  const handleLink = async (row: any, force: boolean = false) => {
    setLinking(row.ml_order_id);
    setError(null);
    try {
      const res: any = await api.post('/warehouse/link-tracking', {
        ml_order_id: row.ml_order_id,
        seller_id: row.seller_id,
        tracking_number: tracking,
        force,
      });
      if (res.success) {
        onLinked();
      } else {
        setError(res.error || 'Error al vincular');
      }
    } catch (e: any) {
      // 409 = ya tiene tracking → mostrar confirm
      if (e.status === 409 || (e.message || '').includes('ya tiene tracking')) {
        setConfirmRow(row);
        setError(e.message);
      } else {
        setError(e.message || 'Error al vincular');
      }
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--color-bg-card)] rounded-2xl shadow-2xl w-full max-w-2xl my-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[var(--color-text)]">Vincular tracking</h2>
            <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
              Tracking: <span className="font-mono font-bold">{tracking}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[var(--color-bg)] shrink-0">
            <X size={20} />
          </button>
        </div>

        {/* Search input */}
        <div className="p-4 border-b border-[var(--color-border)]">
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, ASIN, SKU, pack, order ID, comprador, shipping..."
            className="w-full px-4 py-3 rounded-lg border-2 border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:border-[var(--color-primary)] focus:outline-none"
          />
          <p className="text-[11px] text-[var(--color-text-secondary)] mt-2">
            Mínimo 3 caracteres. Solo se muestran órdenes <b>paid</b> del warehouse.
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
            {confirmRow && (
              <button
                onClick={() => { setConfirmRow(null); handleLink(confirmRow, true); }}
                className="ml-3 px-3 py-1 rounded bg-red-600 text-white text-xs font-bold hover:bg-red-700"
              >
                Sobreescribir
              </button>
            )}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center justify-center py-8 text-[var(--color-text-secondary)]">
              <Loader2 size={20} className="animate-spin mr-2" /> Buscando...
            </div>
          )}
          {!loading && q.trim().length >= 3 && results.length === 0 && (
            <div className="text-center py-8 text-[var(--color-text-secondary)] text-sm">
              No se encontraron órdenes
            </div>
          )}
          {!loading && results.map((r) => (
            <button
              key={`${r.seller_id}-${r.ml_order_id}`}
              onClick={() => handleLink(r)}
              disabled={linking === r.ml_order_id}
              className="w-full text-left flex items-start gap-3 p-3 rounded-lg hover:bg-[var(--color-bg)] border border-transparent hover:border-[var(--color-border)] mb-1 disabled:opacity-50"
            >
              {r.thumbnail ? (
                <img src={hiRes(r.thumbnail) || r.thumbnail} alt="" className="w-14 h-14 rounded object-contain bg-white shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <Package size={20} className="text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-[var(--color-text)] truncate">
                  {r.ml_title || r.amz_title || '—'}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-[var(--color-text-secondary)]">
                  {r.asin && <span className="font-mono">ASIN: <b>{r.asin}</b></span>}
                  {r.quantity > 1 && <span className="font-bold text-orange-500">x{r.quantity}</span>}
                  <span>· Order: <span className="font-mono">{r.ml_order_id}</span></span>
                  {r.pack_id && <span>· Pack: <span className="font-mono">{r.pack_id}</span></span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-[var(--color-text-secondary)]">
                  <User size={10} />
                  <span>{r.buyer_nickname || '—'}</span>
                  {(r.buyer_first_name || r.buyer_last_name) && (
                    <Can cap="global.view_buyers">
                      <span>({[r.buyer_first_name, r.buyer_last_name].filter(Boolean).join(' ')})</span>
                    </Can>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px]">
                  {r.amz_tracking ? (
                    <span className="text-amber-600 dark:text-amber-400">⚠ Ya tiene tracking: {r.amz_tracking}</span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400">Sin tracking</span>
                  )}
                  {r.ship_status && <span>· {r.ship_status}</span>}
                  {r.amazon_status && <span>· AMZ: {r.amazon_status}</span>}
                </div>
              </div>
              {linking === r.ml_order_id ? (
                <Loader2 size={20} className="animate-spin shrink-0 self-center" />
              ) : (
                <Check size={20} className="text-[var(--color-primary)] shrink-0 self-center" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
